import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { useIsManagerView } from '@plato/app-core/hooks/use-view-mode';
import type { AppRegistryPage } from '@plato/app-meta/ai-meta';
import { PageTitle } from '@plato/app-shared/components/page-title';
import { usePageAiMeta } from '@plato/app-shared/hooks/use-page-ai-meta';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import { NewGlobalActivityFeed } from '../../features/activity-feed-new/components/new-global-activity-feed.tsx';
import {
    ActivityFeedScope,
    GlobalActivityFeed,
} from '../../features/activity-feed/components/global-activity-feed.tsx';

export function useActivitiesAiMeta(): AppRegistryPage {
    const { t } = useTranslation();
    return usePageAiMeta({
        path: '/activities',
        title: t('page_title__activities', { ns: 'common' }),
        // The manager navigation shows it only with global analytics; the rep navigation always does.
        requiresByRole: { MANAGER: ['canViewGlobalAnalytics'], SUPER_MANAGER: ['canViewGlobalAnalytics'] },
        description:
            'The feed of logged customer interactions: visit notes, call notes, plain notes, ' +
            'completed tasks and recommendation outcomes, filterable by customer, note type, task ' +
            'status, outcome and free text. It has two modes. A manager in Manager view sees the ' +
            "whole team's activities and can filter by rep; that is the team feed. A rep, and a " +
            'manager who switched to Rep view, sees only their own activities under the name My ' +
            "Activity. For one customer's history, use that customer's detail page.",
    });
}

function Activities(): React.JSX.Element {
    const { canViewGlobalAnalytics, enableNewCustomerDetailView } = useAppConfig();
    const isManagerView = useIsManagerView();

    // Manager view gets the unscoped team feed; Rep view (reps, and managers
    // viewing as a rep) only the signed-in user's own activities.
    const scope = canViewGlobalAnalytics && isManagerView ? ActivityFeedScope.ALL : ActivityFeedScope.OWN;

    const { t } = useTranslation();

    return (
        <>
            <PageTitle title={t('page_title__activities')} />
            {enableNewCustomerDetailView ? (
                <NewGlobalActivityFeed scope={scope} />
            ) : (
                <GlobalActivityFeed scope={scope} />
            )}
        </>
    );
}

export default Activities;
